import { useCallback, useEffect, useRef, useState } from 'react';
import { FlatList, Image, ImageSourcePropType, Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { AudiobookPerson, getPeople } from '../api/audiobooks';
import { RootStackParamList } from '../navigation/RootNavigator';
import { htmlToPlainText } from '../utils/html';
import { personRoleValue } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'PeopleList'>;

type PersonItem = {
  id: number;
  name: string;
  role?: string;
  description?: string | null;
  image: ImageSourcePropType;
};

const fallbackAvatars = [
  require('../../assets/aural-author-1.png'),
  require('../../assets/aural-author-2.png'),
  require('../../assets/aural-author-3.png'),
];

function mapPerson(person: AudiobookPerson, index: number): PersonItem {
  return {
    id: person.id,
    name: person.name,
    role: personRoleValue(person.role),
    description: person.description,
    image: person.avatar ? { uri: person.avatar } : fallbackAvatars[index % fallbackAvatars.length],
  };
}

function comparePeopleOrder(left: AudiobookPerson, right: AudiobookPerson) {
  return (left.order ?? 0) - (right.order ?? 0) || left.name.localeCompare(right.name, 'vi');
}

function roleLabel(role?: string) {
  if (role === 'author') {
    return 'Tác giả';
  }
  if (role === 'narrator') {
    return 'Người đọc sách';
  }
  if (role === 'both') {
    return 'Người đọc sách';
  }
  return 'Tác giả';
}

export default function PeopleListScreen({ navigation, route }: Props) {
  const { width } = useWindowDimensions();
  const cardWidth = (width - 48 - 16) / 2;
  const [items, setItems] = useState<PersonItem[]>([]);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const idsRef = useRef(new Set<number>());

  const loadPage = useCallback((nextPage: number) => {
    if (loadingMore || !hasMore) {
      return;
    }

    setLoadingMore(true);
    getPeople({ per_page: 20, page: nextPage })
      .then((people) => {
        const nextItems = people
          .sort(comparePeopleOrder)
          .filter((person) => !idsRef.current.has(person.id))
          .map((person, index) => mapPerson(person, items.length + index));

        nextItems.forEach((person) => idsRef.current.add(person.id));
        setItems((current) => [...current, ...nextItems]);
        setPage(nextPage);
        setHasMore(people.length >= 20 && nextItems.length > 0);
        setLoaded(true);
      })
      .catch(() => {
        setHasMore(false);
        setLoaded(true);
      })
      .finally(() => {
        setLoadingMore(false);
      });
  }, [hasMore, items.length, loadingMore]);

  useEffect(() => {
    idsRef.current = new Set<number>();
    setItems([]);
    setPage(1);
    setLoaded(false);
    setHasMore(true);
    loadPage(1);
  }, []);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadPage(page + 1);
    }
  };

  return (
    <View style={styles.screen}>
      <FlatList
        data={items}
        keyExtractor={(item) => String(item.id)}
        numColumns={2}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.container}
        columnWrapperStyle={styles.row}
        onEndReached={loadMore}
        onEndReachedThreshold={0.35}
        ListHeaderComponent={(
          <>
            <ActionHeader
              onBack={() => navigation.goBack()}
              rightIcons={['search-outline', 'ellipsis-horizontal']}
              onRightPress={[() => navigation.navigate('GetSuggestions')]}
            />
            <Text style={styles.title}>{route.params?.title ?? 'Người đọc sách'}</Text>
            <Text style={styles.subtitle}>{loaded ? `${items.length} người đọc sách` : 'Đang tải dữ liệu'}</Text>
          </>
        )}
        renderItem={({ item }) => (
          <Pressable style={[styles.card, { width: cardWidth }]} onPress={() => navigation.navigate('AuthorDetails', { id: item.id })}>
            <Image source={item.image} style={styles.avatar} resizeMode="cover" />
            <Text style={styles.name} numberOfLines={2}>{item.name}</Text>
            <Text style={styles.role} numberOfLines={1}>{roleLabel(item.role)}</Text>
            {item.description ? <Text style={styles.description} numberOfLines={2}>{htmlToPlainText(item.description)}</Text> : null}
          </Pressable>
        )}
        ListFooterComponent={loadingMore && items.length ? <Text style={styles.loadingMore}>Đang tải thêm...</Text> : null}
        ListEmptyComponent={loaded ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>Chưa có người đọc sách</Text>
            <Text style={styles.emptyText}>Dữ liệu sẽ hiển thị khi API trả về danh sách.</Text>
          </View>
        ) : (
          <View style={styles.skeletonGrid}>
            {[0, 1, 2, 3, 4, 5].map((item) => (
              <View key={item} style={[styles.skeletonCard, { width: cardWidth }]}>
                <View style={styles.skeletonAvatar} />
                <View style={styles.skeletonTitle} />
                <View style={styles.skeletonText} />
              </View>
            ))}
          </View>
        )}
      />

      <BottomTabBar
        active="My Library"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingTop: 62,
    paddingHorizontal: 24,
    paddingBottom: 34,
  },
  title: {
    color: colors.textPrimary,
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '800',
    marginBottom: 9,
  },
  subtitle: {
    color: colors.textSecondary,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
    marginBottom: 25,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  card: {
    minHeight: 196,
    borderRadius: 22,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    padding: 14,
    borderWidth: 3,
    borderColor: '#FFFFFF',
    shadowColor: '#39BFFF',
    shadowOpacity: 0.14,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },
  avatar: {
    width: 94,
    height: 94,
    borderRadius: 28,
    backgroundColor: colors.card,
    borderWidth: 3,
    borderColor: colors.yellow,
    marginBottom: 10,
  },
  name: {
    color: colors.textPrimary,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 4,
  },
  role: {
    color: colors.button,
    fontSize: 11,
    fontWeight: '800',
    marginBottom: 6,
  },
  description: {
    color: colors.textSecondary,
    fontSize: 11,
    lineHeight: 15,
    textAlign: 'center',
  },
  loadingMore: {
    color: colors.textSecondary,
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 16,
  },
  skeletonGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  skeletonCard: {
    alignItems: 'center',
    marginBottom: 18,
    padding: 14,
  },
  skeletonAvatar: {
    width: 94,
    height: 94,
    borderRadius: 28,
    backgroundColor: '#ECEEF2',
    marginBottom: 12,
  },
  skeletonTitle: {
    width: '80%',
    height: 16,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
    marginBottom: 8,
  },
  skeletonText: {
    width: '56%',
    height: 12,
    borderRadius: 6,
    backgroundColor: '#ECEEF2',
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyTitle: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 13,
    textAlign: 'center',
  },
});
